'use client';

import { useEffect, useRef, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { cn } from '@/shared/lib/cn';

/** Width of the revealed trailing delete action (Android `RevealActionWidth` parity). */
const ACTION_WIDTH = 88;
/** Pointer travel before we commit to a horizontal swipe vs. a vertical scroll
 *  / long-press reorder (touch-slop discrimination, Android parity). */
const SLOP = 8;

interface SwipeToRevealRowProps {
  children: React.ReactNode;
  /** Tear down the row (the trailing red action). */
  onDelete: () => void;
  /** Only one row stays open at a time — the open state is hoisted to the list. */
  revealed: boolean;
  onRevealedChange: (open: boolean) => void;
  /** Accessible label for the delete action. */
  deleteLabel: string;
  disabled?: boolean;
}

/**
 * iOS/Android-parity swipe-to-delete row: swipe the content toward the start to
 * reveal a red delete button at the trailing edge; tap the button to delete (no
 * dismiss-on-swipe — a deliberate guard, exactly like the native lists). Tapping
 * the content while open closes it (a transparent overlay swallows the tap so it
 * can't reach the editable fields underneath).
 *
 * This is the web replacement for the old per-row trash icon: the estimate row
 * carries no delete glyph and no drag handle anymore — delete is this swipe and
 * reorder is the row's own long-press lift (dnd-kit delay activation), matching
 * Apple/Android where the gestures own those affordances.
 *
 * Uses Pointer Events so the same gesture works for touch and mouse. Direction
 * is logical (RTL-aware): "toward the start" is left in LTR, right in RTL, and
 * the action pins to the trailing edge via `inset-inline-end`.
 */
export function SwipeToRevealRow({
  children,
  onDelete,
  revealed,
  onRevealedChange,
  deleteLabel,
  disabled = false,
}: SwipeToRevealRowProps) {
  // Live translateX of the content layer. Negative = revealed in LTR, positive
  // in RTL. Snaps to the open/closed target via CSS transition when not actively
  // dragging (`dragging` disables the transition for 1:1 finger tracking).
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);

  const startX = useRef(0);
  const startY = useRef(0);
  const startOffset = useRef(0);
  // null until the gesture direction is decided; then 'x' (we own it) or 'y'
  // (a scroll / reorder long-press — we bail and let it through).
  const axis = useRef<'x' | 'y' | null>(null);
  const isRtl = useRef(false);
  const moved = useRef(false);

  // Snap to the hoisted open/closed state whenever it (or RTL) changes and we're
  // not mid-drag — keeps every row in sync when the list closes others.
  useEffect(() => {
    if (dragging) return;
    setOffset(revealed ? (isRtl.current ? ACTION_WIDTH : -ACTION_WIDTH) : 0);
  }, [revealed, dragging]);

  // Safety net: if the pointer is released off this element (or another widget,
  // e.g. the dnd-kit reorder sensor, steals pointer capture so our own
  // pointerup never fires), a window-level listener still ends the drag and
  // snaps the row back. Without it a half-swiped row could stay frozen open
  // with the delete action peeking out.
  useEffect(() => {
    if (!dragging) return;
    const end = () => {
      setDragging(false);
      onRevealedChange(Math.abs(offset) > ACTION_WIDTH / 2);
      axis.current = null;
    };
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    return () => {
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
    };
  }, [dragging, offset, onRevealedChange]);

  const clampOffset = (value: number) =>
    isRtl.current
      ? Math.min(Math.max(value, 0), ACTION_WIDTH)
      : Math.max(Math.min(value, 0), -ACTION_WIDTH);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (disabled || e.button !== 0) return;
    // Never hijack a gesture that begins on an editable / interactive control:
    // most of this row is inputs (name, quantity, price, unit), and dragging
    // inside one is text selection or a native interaction, NOT a swipe. Without
    // this guard a horizontal drag to select text reveals the delete action and
    // can leave the content layer stuck at a partial offset. Swipe still works
    // when started on the row's non-field surface (padding, totals).
    if (
      (e.target as HTMLElement).closest(
        'input, textarea, select, button, [contenteditable="true"]'
      )
    ) {
      axis.current = 'y';
      return;
    }
    startX.current = e.clientX;
    startY.current = e.clientY;
    startOffset.current = offset;
    axis.current = null;
    moved.current = false;
    isRtl.current =
      getComputedStyle(e.currentTarget).direction === 'rtl';
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (disabled) return;
    // Only track a drag while the primary button is actually held. Plain mouse
    // movement (hover) also fires pointermove with `buttons === 0`; without this
    // guard the stale startX from the last pointerdown makes a hover read as a
    // swipe, revealing the delete action just by moving the cursor over rows.
    if (axis.current !== 'x' && !(e.buttons & 1)) return;
    const dx = e.clientX - startX.current;
    const dy = e.clientY - startY.current;

    if (axis.current === null) {
      if (Math.abs(dx) < SLOP && Math.abs(dy) < SLOP) return;
      // Vertical wins → a scroll or a reorder long-press: stay out of the way.
      axis.current = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (axis.current === 'x') {
        setDragging(true);
        e.currentTarget.setPointerCapture(e.pointerId);
      }
    }

    if (axis.current === 'x') {
      moved.current = true;
      // Stop the browser from selecting text / starting a native scroll.
      e.preventDefault();
      setOffset(clampOffset(startOffset.current + dx));
    }
  };

  const finishGesture = (e: React.PointerEvent<HTMLDivElement>) => {
    if (axis.current === 'x') {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      // Past the halfway point → open, else snap back closed (Android threshold).
      const shouldOpen = Math.abs(offset) > ACTION_WIDTH / 2;
      setDragging(false);
      onRevealedChange(shouldOpen);
    }
    axis.current = null;
  };

  return (
    <div className="relative overflow-hidden">
      {/* Trailing delete action, pinned to the inline-end edge and revealed as
          the content slides away. Tap to delete — never dismiss-on-swipe. */}
      <button
        type="button"
        onClick={() => {
          onDelete();
          onRevealedChange(false);
        }}
        disabled={disabled}
        aria-label={deleteLabel}
        tabIndex={revealed ? 0 : -1}
        className="absolute inset-y-0 end-0 flex items-center justify-center bg-destructive text-destructive-foreground"
        style={{ width: ACTION_WIDTH }}
      >
        <Trash2 className="size-5" strokeWidth={1.5} />
      </button>

      {/* Content layer that slides over the action. `pan-y` lets vertical
          scroll / the reorder long-press through while we own horizontal. */}
      <div
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishGesture}
        onPointerCancel={finishGesture}
        className={cn(
          'relative bg-estimate-paper',
          !dragging && 'transition-transform duration-200 ease-out'
        )}
        style={{ transform: `translateX(${offset}px)`, touchAction: 'pan-y' }}
      >
        {children}

        {/* When open, a transparent overlay swallows the first tap to close the
            row instead of letting it reach the editable fields underneath. */}
        {revealed && (
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            onClick={() => onRevealedChange(false)}
            className="absolute inset-0 cursor-default"
          />
        )}
      </div>
    </div>
  );
}
